import { useCallback, useEffect, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Cell, Column, Row, SortingRule } from 'react-table'
import {
  dealerConfigureProcess,
  StatusOption,
} from '@racepointenergy/swc/scs/dealer-configure-process'
import type { ProcessUpdate } from '@racepointenergy/swc/scs/types/host'
import { Status } from '@racepointenergy/swc/scs/types/host'
import _isNil from 'lodash/isNil'

import { CommonSearch } from 'components/Table'
import ConfirmModal from 'components/Modal/ConfirmModal'
import { Spinner } from 'components/Loader'
import { MobileSortBy, Table } from 'components/Table'

import {
  ProcessesRowActionIcon,
  ProcessesStatusLabel,
  ProcessesSearchBox,
  StyledProcessActionCell,
} from '../styles'
import { LogLevelOption } from '../types'
import {
  requiresSystemAccessMsg,
  restartAction,
  startAction,
  stopAction,
} from '../consts'

import {
  CellHomeProcessInfoStatusStyles,
  CellHomeProcessInfoStyles,
  CellHomeProcessNameStyles,
  CellHomeProcessProcessorInfoStatusStyles,
  ProcessLogLevelDropdown,
  ProcessLogLevelWrapper,
} from './styles'
import { logLevelMessages, processMessages } from '../messages'
import { commonMessages } from 'app/i18n/messages'
import MobileProcessViewComponent from './mobileProcessViewComponent'
import { canProcessBeStopped } from './processHelper'
import { PARENT_PROCESSES, PROCESS_INFO_HEADER } from './consts'
import { getSortOptions, sortByInsensitiveString } from 'components/Table/utils'
import { useAppSelector } from 'app/hooks'
import { selectedHomeSelector } from 'features/home/selectors'
import { hasSystemAccessEnabled } from 'features/home/utils'
import Tooltip from 'components/Tooltip'

const DEFAULT_SORT_BY = { id: 'ApplicationName', desc: false }

interface HomeProcessInfoTableProps {
  homeId: string
  data: ProcessUpdate[]
  onSelect: (row: Row<ProcessUpdate>) => void
}

type LogLevelRowPrevState = Pick<ProcessUpdate, 'LogLevel' | 'ApplicationName'>

const StatusCell = (cell: Cell<ProcessUpdate>) => {
  const status = cell.row.original.Status
  return (
    <ProcessesStatusLabel isRunning={!status}>
      {Status[status as number]}
    </ProcessesStatusLabel>
  )
}
const IdCell = (cell: Cell<ProcessUpdate>) => {
  return <b>{cell.row.original.ProcessIdentifier}</b>
}

const CPUCell = (cell: Cell<ProcessUpdate>) => {
  return <b>{cell.row.original.CpuUsage?.toFixed(2)}</b>
}

const NameCell = (cell: Cell<ProcessUpdate>) => {
  return <b>{cell.row.original.ApplicationName}</b>
}

// TODO localization
export const HomeProcessTable = (props: HomeProcessInfoTableProps) => {
  const [
    updateConfigureProcess,
    { isLoading: mutationIsLoading, originalArgs: mutationArgs },
  ] = dealerConfigureProcess.useMutation()
  const { formatMessage } = useIntl()
  const [searchValue, setSearchValue] = useState<string>()
  const [isResetProcess, setResetProcess] = useState(false)
  const [processName, setProcessName] = useState<string>()
  const [action, setAction] = useState<StatusOption>()
  const [logLevelRow, setLogLevelRow] = useState<LogLevelRowPrevState[]>([])

  const currentHome = useAppSelector(selectedHomeSelector.data)

  const hasSystemAccess = hasSystemAccessEnabled(currentHome)

  const logLevelOptions = useMemo(
    () => [
      { label: formatMessage(logLevelMessages.emergency), value: 0 },
      { label: formatMessage(logLevelMessages.alert), value: 1 },
      { label: formatMessage(logLevelMessages.critical), value: 2 },
      { label: formatMessage(logLevelMessages.error), value: 3 },
      { label: formatMessage(logLevelMessages.warning), value: 4 },
      { label: formatMessage(logLevelMessages.notice), value: 5 },
      { label: formatMessage(logLevelMessages.info), value: 6 },
      { label: formatMessage(logLevelMessages.debug), value: 7 },
    ],
    [formatMessage]
  )

  useEffect(() => {
    mutationIsLoading && setLogLevelRow([])
  }, [mutationIsLoading])

  const actionCallBack = useCallback(
    (name: string, status: StatusOption) => {
      setAction(status)
      setProcessName(name)
      if (status === StatusOption.restart) {
        setResetProcess(true)
      } else {
        if (name) {
          updateConfigureProcess({
            id: props.homeId,
            status: status ?? 0,
            params: [{ process: name }],
          })
        }
      }
    },
    [
      setAction,
      setProcessName,
      setResetProcess,
      updateConfigureProcess,
      props.homeId,
    ]
  )

  const handleStopPropagation = useCallback((e) => {
    e.stopPropagation()
  }, [])

  const LogLevelCell = useCallback(
    (cell: Cell<ProcessUpdate>) => {
      const config = cell.row.original
      const { ApplicationName, LogLevel, ProcessIdentifier } = config

      if (!_isNil(LogLevel) && !_isNil(ApplicationName)) {
        const handleLogLevelChange = (option: LogLevelOption) => {
          setLogLevelRow((prev) => [
            ...prev,
            {
              ApplicationName,
              LogLevel,
            } as LogLevelRowPrevState,
          ])
          updateConfigureProcess({
            id: props.homeId,
            params: [
              {
                pid: ProcessIdentifier,
                process: ApplicationName,
                level: option.value,
              },
            ],
          })
        }

        const foundLogLevel = logLevelRow.find(
          (item) => item.ApplicationName === ApplicationName
        )
        const logLevelValue = logLevelOptions.find(
          (e) => e.value === (foundLogLevel ? foundLogLevel.LogLevel : LogLevel)
        )

        return (
          <ProcessLogLevelWrapper onClick={handleStopPropagation}>
            {hasSystemAccess ? (
              <ProcessLogLevelDropdown
                name="logLevel"
                options={logLevelOptions}
                value={logLevelValue!}
                disabled={!_isNil(foundLogLevel) || !hasSystemAccess}
                onChange={handleLogLevelChange}
              />
            ) : (
              <Tooltip
                showOn="hover"
                positions={['left']}
                fullWidth
                content={<>{requiresSystemAccessMsg}</>}
              >
                <ProcessLogLevelDropdown
                  name="logLevel"
                  options={logLevelOptions}
                  value={logLevelValue!}
                  disabled={!_isNil(foundLogLevel) || !hasSystemAccess}
                  onChange={handleLogLevelChange}
                />
              </Tooltip>
            )}
            {foundLogLevel && <Spinner size={1} />}
          </ProcessLogLevelWrapper>
        )
      }
      return null
    },
    [
      logLevelRow,
      logLevelOptions,
      handleStopPropagation,
      hasSystemAccess,
      updateConfigureProcess,
      props.homeId,
    ]
  )

  const ProcessActionCell = useCallback(
    (cell: Cell<ProcessUpdate>) => {
      const config = cell.row.original
      const { ApplicationName, Status: ProcessStatus } = config
      const processIsUpdating =
        mutationIsLoading &&
        mutationArgs?.params?.[0]?.process === ApplicationName
      if (processIsUpdating) {
        return (
          <StyledProcessActionCell>
            <Spinner size={1} />
          </StyledProcessActionCell>
        )
      }

      if (
        !_isNil(ApplicationName) &&
        !canProcessBeStopped(ApplicationName) &&
        !PARENT_PROCESSES.includes(ApplicationName)
      ) {
        return (
          <StyledProcessActionCell>
            {ApplicationName && (
              <ProcessesRowActionIcon
                size={32}
                src={restartAction.iconUrl}
                onClick={(e) => {
                  e.stopPropagation()
                  hasSystemAccess &&
                    actionCallBack(ApplicationName, StatusOption.restart)
                }}
                $disabled={!hasSystemAccess}
              />
            )}
          </StyledProcessActionCell>
        )
      }
      const selectAction = (processStatus: Status): JSX.Element => {
        switch (processStatus) {
          case Status.Running:
            return (
              <StyledProcessActionCell>
                {ApplicationName && (
                  <ProcessesRowActionIcon
                    size={32}
                    src={stopAction.iconUrl}
                    onClick={(e) => {
                      e.stopPropagation()
                      hasSystemAccess &&
                        actionCallBack(ApplicationName, StatusOption.stop)
                    }}
                    $disabled={!hasSystemAccess}
                  />
                )}
              </StyledProcessActionCell>
            )
          case Status.Stopped:
            return (
              <StyledProcessActionCell>
                {ApplicationName && (
                  <ProcessesRowActionIcon
                    size={32}
                    src={startAction.iconUrl}
                    onClick={(e) => {
                      e.stopPropagation()
                      hasSystemAccess &&
                        actionCallBack(ApplicationName, StatusOption.start)
                    }}
                    $disabled={!hasSystemAccess}
                  />
                )}
              </StyledProcessActionCell>
            )
          default:
            return <></>
        }
      }

      return (
        <>
          {!_isNil(ApplicationName) &&
            !_isNil(ProcessStatus) &&
            !PARENT_PROCESSES.includes(ApplicationName) &&
            selectAction(ProcessStatus)}
        </>
      )
    },
    [actionCallBack, hasSystemAccess, mutationIsLoading, mutationArgs]
  )

  const headers = useMemo(
    () => ({
      id: {
        label: 'Id',
        id: PROCESS_INFO_HEADER.ID,
      },
      name: {
        label: formatMessage(processMessages.processName),
        id: PROCESS_INFO_HEADER.NAME,
      },
      state: {
        label: formatMessage(processMessages.processStatus),
        id: PROCESS_INFO_HEADER.STATUS,
      },
      cpu: {
        label: formatMessage(processMessages.CPUUsage),
        id: PROCESS_INFO_HEADER.CPU,
      },
    }),
    [formatMessage]
  )
  const sortOptions = useMemo(() => getSortOptions(headers), [headers])

  const [sortBy, setSortBy] =
    useState<SortingRule<ProcessUpdate>>(DEFAULT_SORT_BY)

  const columns: readonly Column<ProcessUpdate>[] = useMemo(
    () => [
      {
        Header: 'ID',
        accessor: 'ProcessIdentifier',
        Cell: IdCell,
        cellStyles: CellHomeProcessInfoStyles,
        maxWidth: 10,
      },
      {
        Header: 'Name',
        accessor: 'ApplicationName',
        maxWidth: 40,
        cellStyles: CellHomeProcessNameStyles,
        Cell: NameCell,
        sortType: sortByInsensitiveString,
      },
      {
        disableGlobalFilter: true,
        Header: 'Status',
        accessor: 'Status',
        maxWidth: 20,
        cellStyles: CellHomeProcessInfoStatusStyles,
        Cell: StatusCell,
      },
      {
        disableGlobalFilter: true,
        Header: 'Log Level',
        accessor: 'LogLevel',
        maxWidth: 45,
        cellStyles: CellHomeProcessInfoStatusStyles,
        Cell: LogLevelCell,
      },
      {
        disableGlobalFilter: true,
        Header: '% of CPU',
        accessor: 'CpuUsage',
        maxWidth: 10,
        cellStyles: CellHomeProcessProcessorInfoStatusStyles,
        Cell: CPUCell,
      },
      {
        disableGlobalFilter: true,
        disableSortBy: true,
        maxWidth: 10,
        id: 'start',
        cellStyles: CellHomeProcessInfoStyles,
        Cell: ProcessActionCell,
      },
    ],
    [ProcessActionCell, LogLevelCell]
  )

  return (
    <>
      <ConfirmModal
        isOpen={isResetProcess}
        title={formatMessage(processMessages.restartProcessTitle)}
        confirmBtnTitle={formatMessage(processMessages.confirmRestartButton)}
        discardBtnTitle={formatMessage(commonMessages.cancel)}
        variant="primary"
        onConfirm={() => {
          if (processName)
            updateConfigureProcess({
              id: props.homeId,
              status: action ?? 0,
              params: [{ process: processName }],
            })

          setResetProcess(false)
        }}
        onDiscard={() => setResetProcess(false)}
      >
        <FormattedMessage {...processMessages.restartProcessBody} />
      </ConfirmModal>
      <MobileSortBy<ProcessUpdate>
        headers={headers}
        sortOptions={sortOptions}
        sortBy={sortBy}
        setSortBy={setSortBy}
      />
      <ProcessesSearchBox>
        <CommonSearch
          setSearchValue={setSearchValue}
          placeholder={formatMessage(processMessages.searchProcess)}
        />
      </ProcessesSearchBox>

      <Table
        columns={columns}
        data={props.data}
        defaultSortBy={DEFAULT_SORT_BY}
        searchValue={searchValue}
        sortBy={sortBy}
        setSortBy={setSortBy}
        onRowClick={props.onSelect}
        isPaginated={false}
        stripedRows
        mobileViewComponent={MobileProcessViewComponent}
        fieldAsUniqueKey={'id'}
      />
    </>
  )
}
